import { INDONESIA_REGENCIES } from "../data/regencies";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, apiErr, STATUS_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RiskBadge, ProgressBar } from "@/components/ProjectMetrics";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, FolderKanban, UserCheck, Search, X } from "lucide-react";

const empty = {
  name: "", client: "", client_code: "", description: "", location: "", estimated_volume: 0,
  start_date: "", target_completion_date: "", estimated_volume: 0, daily_target: 0, working_days: 0,
  total_volume_ml: 0, bidang_list: "", notes: "",
};

export default function Projects() {
  const [clientList, setClientList] = useState([]);
  const [showClientModal, setShowClientModal] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", code: "", category: "BUMN", location: "" });
  const [savingClient, setSavingClient] = useState(false);

  async function handleCreateClient() {
    if (!newClient.name || !newClient.code) {
      alert("Nama dan Kode Klien wajib diisi!");
      return;
    }
    setSavingClient(true);
    try {
      const res = await api.post("/clients", newClient);
      const created = res.data?.data || res.data || newClient;

      // Muat ulang daftar klien
      try {
        const cRes = await api.get("/clients");
        const list = Array.isArray(cRes.data) ? cRes.data : (cRes.data?.data || []);
        setClientList(list);
      } catch (e) {
        console.error("Gagal refresh client list:", e);
      }

      // Pasang otomatis ke form proyek
      setForm((prev) => ({
        ...prev,
        client: created.name || newClient.name,
        client_code: created.code || newClient.code,
        location: created.location || newClient.location || prev.location,
      }));

      setShowClientModal(false);
      setNewClient({ name: "", code: "", category: "BUMN", location: "" });
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || "Gagal menambahkan klien");
    } finally {
      setSavingClient(false);
    }
  }
  useEffect(() => {
    async function loadClients() {
      try {
        const res = await api.get("/clients");
        console.log("[DEBUG] Respon API Klien:", res.data);
        if (Array.isArray(res.data)) {
          setClientList(res.data);
        } else if (res.data && Array.isArray(res.data.data)) {
          setClientList(res.data.data);
        }
      } catch (err) {
        console.error("[DEBUG] Gagal memuat klien:", err);
      }
    }
    loadClients();
  }, []);
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [wd, setWd] = useState(null);
  const [dateErr, setDateErr] = useState("");
  const [tabFilter, setTabFilter] = useState("ALL"); // ALL, MINE, ACTIVE
  const [searchQuery, setSearchQuery] = useState("");
  const canCreate = ["SUPER_ADMIN", "MANAGER_OPERASIONAL"].includes(user?.role);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  // auto-calculate total working days (Mon-Sat excl. Sunday & national holidays)
  useEffect(() => {
    const { start_date, target_completion_date } = form;
    setDateErr("");
    if (!start_date || !target_completion_date) { setWd(null); return; }
    if (target_completion_date < start_date) {
      setWd(null); setDateErr("Tanggal selesai tidak boleh lebih awal dari tanggal mulai."); return;
    }
    const t = setTimeout(() => {
      api.get(`/projects/working-days?start=${start_date}&end=${target_completion_date}`)
        .then((r) => { setWd(r.data.working_days); setForm((f) => ({ ...f, working_days: r.data.working_days })); })
        .catch(() => setWd(null));
    }, 300);
    return () => clearTimeout(t);
  }, [form.start_date, form.target_completion_date]);

  const create = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_volume: +form.estimated_volume,
        daily_target: +form.daily_target,
        working_days: +form.working_days,
        total_volume_ml: +form.total_volume_ml,
        bidang_list: form.bidang_list.split(",").map((s) => s.trim()).filter(Boolean)
      };
      const { data } = await api.post("/projects", payload);
      toast.success(`Proyek ${data.code} dibuat`);
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const isManagerOrAdmin = ["SUPER_ADMIN", "MANAGER_OPERASIONAL"].includes(user?.role);
  const myProjectsCount = (projects || []).filter((p) => p.pimpinan_id === user?.id).length;

  const q = searchQuery.trim().toLowerCase();
  const displayedProjects = (projects || []).filter((p) => {
    if (tabFilter === "MINE" && p.pimpinan_id !== user?.id) return false;
    if (tabFilter === "ACTIVE" && p.status === "completed") return false;
    if (q) {
      const matchCode = (p.code || "").toLowerCase().includes(q);
      const matchName = (p.name || "").toLowerCase().includes(q);
      const matchClient = (p.client || "").toLowerCase().includes(q);
      return matchCode || matchName || matchClient;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground">Manajemen Proyek</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Proyek</h1>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="new-project-btn"><Plus className="h-4 w-4 mr-1.5" /> Proyek Baru</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
              <DialogHeader><DialogTitle className="font-heading">Buat Proyek Baru</DialogTitle></DialogHeader>
                <div className="grid sm:grid-cols-2 gap-4 py-2">
                  <Field label="Nama Proyek" v={form.name} on={set("name")} testid="p-name" />

                  {/* Poin 5: Dropdown Master Klien */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Pilih Klien / Instansi</Label>
                      <button
                        type="button"
                        onClick={() => setShowClientModal((prev) => !prev)}
                        className="text-xs text-primary font-semibold hover:underline"
                      >
                        {showClientModal ? "← Pilih dari Daftar" : "+ Tambah Klien Baru"}
                      </button>
                    </div>

                    {!showClientModal ? (
                      <select
                        className="w-full px-3 py-2 border rounded-md text-sm bg-background"
                        value={form.client}
                        onChange={(e) => {
                          const selectedName = e.target.value;
                          const c = clientList.find((x) => x.name === selectedName);
                          setForm((prev) => ({
                            ...prev,
                            client: selectedName,
                            client_code: c ? c.code : prev.client_code,
                            location: c && c.location ? c.location : prev.location,
                          }));
                        }}
                      >
                        <option value="">-- Pilih Instansi Klien --</option>
                        {clientList.map((c) => (
                          <option key={c.id || c.code} value={c.name}>
                            {c.name} ({c.code})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="p-3 border rounded-lg bg-muted/40 space-y-2 border-primary/40">
                        <p className="text-xs font-bold text-primary">Form Instansi Klien Baru</p>
                        <input
                          type="text"
                          placeholder="Nama Instansi (cth: RSUD Moh Anwar)"
                          value={newClient.name}
                          onChange={(e) => setNewClient((prev) => ({ ...prev, name: e.target.value }))}
                          className="w-full px-2.5 py-1.5 border rounded text-xs bg-background"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            placeholder="Kode (cth: RSUD-SMN)"
                            value={newClient.code}
                            onChange={(e) => setNewClient((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                            className="w-full px-2.5 py-1.5 border rounded text-xs bg-background"
                          />
                          <select
                            value={newClient.category}
                            onChange={(e) => setNewClient((prev) => ({ ...prev, category: e.target.value }))}
                            className="w-full px-2.5 py-1.5 border rounded text-xs bg-background"
                          >
                            <option value="BUMN">BUMN</option>
                            <option value="Pemerintah Daerah">Pemerintah Daerah</option>
                            <option value="Kementerian">Kementerian</option>
                            <option value="Swasta">Swasta</option>
                          </select>
                        </div>
                        <input
                          type="text"
                          placeholder="Lokasi / Kota (cth: Kabupaten Sumenep)"
                          value={newClient.location}
                          onChange={(e) => setNewClient((prev) => ({ ...prev, location: e.target.value }))}
                          className="w-full px-2.5 py-1.5 border rounded text-xs bg-background"
                        />
                        <div className="flex justify-end gap-1.5 pt-1">
                          <button
                            type="button"
                            onClick={() => setShowClientModal(false)}
                            className="px-2.5 py-1 text-xs border rounded hover:bg-background"
                          >
                            Batal
                          </button>
                          <button
                            type="button"
                            onClick={handleCreateClient}
                            disabled={savingClient}
                            className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded font-medium disabled:opacity-50"
                          >
                            {savingClient ? "Menyimpan..." : "Simpan Klien"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <Field label="Kode Klien" v={form.client_code} on={set("client_code")} testid="p-clientcode" />

                  {/* Poin 1: Dropdown Pencarian 514 Kabupaten / Kota */}
                  <div className="space-y-2">
                    <Label>Lokasi (Kabupaten / Kota)</Label>
                    <input
                      list="regencies-list"
                      className="w-full px-3 py-2 border rounded-md text-sm bg-background"
                      placeholder="Ketik untuk mencari Kota/Kab..."
                      value={form.location}
                      onChange={set("location")}
                      data-testid="p-location"
                    />
                    <datalist id="regencies-list">
                      {INDONESIA_REGENCIES.map((reg, idx) => (
                        <option key={idx} value={reg} />
                      ))}
                    </datalist>
                  </div>

                  <Field label="Tanggal Mulai" type="date" v={form.start_date} on={set("start_date")} testid="p-start" />
                  <Field label="Target Selesai" type="date" v={form.target_completion_date} on={set("target_completion_date")} testid="p-target" />

                  {/* Poin 2: Input Berdampingan mL & Estimasi Berkas */}
                  <Field label="Target Volume (mL)" type="number" step="0.01" v={form.total_volume_ml} on={set("total_volume_ml")} testid="p-volume-ml" />
                  <Field label="Estimasi Jumlah Berkas" type="number" v={form.estimated_volume} on={set("estimated_volume")} testid="p-estimated-volume" />

                  <div className="space-y-2 sm:col-span-2">
                    <Label>Total Hari Kerja (otomatis)</Label>
                    <Input value={wd != null ? `${wd} hari kerja` : "—"} readOnly disabled data-testid="p-workdays" className="bg-muted/40" />
                    <p className="text-[10px] text-muted-foreground">Senin–Sabtu, tanpa Minggu & hari libur nasional.</p>
                  </div>

                  {/* Poin 2 & 3: Kalkulator Rasio & Estimasi Output Lapangan Real-time */}
                  {+form.total_volume_ml > 0 && +form.estimated_volume > 0 && (
                    <div className="sm:col-span-2 p-3 bg-red-50/60 border border-red-200 rounded-lg text-xs text-red-950 space-y-1">
                      <div className="font-semibold text-red-900">Kalkulasi Standar Lapangan (Dinamis):</div>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11.5px]">
                        <span>Rasio Proyek: <strong>1 mL ≈ {(+form.estimated_volume / +form.total_volume_ml).toFixed(1)} berkas</strong></span>
                        {wd > 0 && (
                          <>
                            <span>Target Tim / Hari: <strong>{(+form.estimated_volume / wd).toFixed(0)} berkas/hari</strong> (≈ {(+form.total_volume_ml / wd).toFixed(2)} mL)</span>
                            <span>Estimasi per Orang (asumsi 6 personil): <strong>{Math.ceil((+form.estimated_volume / wd) / 6)} berkas/hari</strong></span>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {dateErr && <p className="sm:col-span-2 text-xs text-red-600" data-testid="p-date-error">{dateErr}</p>}

                  {/* Poin 4: Sub-Proyek / Bidang Unit Kerja */}
                  <div className="sm:col-span-2 space-y-2">
                    <Label>Daftar Sub-Proyek / Unit Kerja (pisahkan dengan koma)</Label>
                    <Input value={form.bidang_list} onChange={set("bidang_list")} placeholder="Keuangan, Klaim, Teknik" data-testid="p-bidang" />
                    <p className="text-[10px] text-muted-foreground">Setiap unit kerja akan otomatis dibuatkan folder kerja terisolasi untuk mencegah duplikasi berkas.</p>
                  </div>

                  <div className="sm:col-span-2 space-y-2">
                    <Label>Deskripsi</Label>
                    <Textarea value={form.description} onChange={set("description")} data-testid="p-desc" />
                  </div>
                </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
                <Button onClick={create} disabled={saving || !!dateErr || !form.name || !form.client || !form.start_date || !form.target_completion_date} data-testid="p-submit">
                  {saving ? "Menyimpan..." : "Simpan"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Bar Filter & Pencarian */}
      {projects && projects.length > 0 && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-b border-border pb-3">
          {isManagerOrAdmin ? (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
              <button
                type="button"
                onClick={() => setTabFilter("ALL")}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap transition-all ${
                  tabFilter === "ALL"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-muted/60 text-muted-foreground hover:bg-muted"
                }`}
              >
                Semua Proyek ({projects.length})
              </button>
              <button
                type="button"
                onClick={() => setTabFilter("MINE")}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full flex items-center gap-1.5 whitespace-nowrap transition-all ${
                  tabFilter === "MINE"
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-muted/60 text-muted-foreground hover:bg-muted"
                }`}
              >
                <UserCheck className="h-3.5 w-3.5" />
                Dipimpin Saya ({myProjectsCount})
              </button>
              <button
                type="button"
                onClick={() => setTabFilter("ACTIVE")}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap transition-all ${
                  tabFilter === "ACTIVE"
                    ? "bg-emerald-600 text-white shadow-sm"
                    : "bg-muted/60 text-muted-foreground hover:bg-muted"
                }`}
              >
                Proyek Aktif
              </button>
            </div>
          ) : <div />}

          {/* Input Search Proyek */}
          <div className="relative min-w-[240px] sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cari kode, nama, klien..."
              className="pl-9 pr-8 h-9 text-xs rounded-full bg-background"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {!projects ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-52" />)}</div>
      ) : displayedProjects.length === 0 ? (
        <Card className="p-16 text-center">
          <FolderKanban className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-3 font-semibold">Tidak ada proyek yang sesuai</p>
          <p className="text-sm text-muted-foreground">
            {searchQuery
              ? `Tidak ditemukan proyek yang cocok dengan kata kunci "${searchQuery}".`
              : tabFilter === "MINE"
              ? "Anda belum ditugaskan sebagai pimpinan di proyek manapun."
              : "Belum ada data proyek."}
          </p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {displayedProjects.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
              <Card className="p-5 cursor-pointer hover:-translate-y-[3px] hover:shadow-md transition-transform duration-200 h-full flex flex-col"
                onClick={() => nav(`/projects/${p.id}`)} data-testid={`project-card-${p.code}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-mono text-[11px] text-primary font-semibold">{p.code}</p>
                      {p.pimpinan_id === user?.id && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-200">
                          Pimpinan: Anda
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading font-semibold text-base leading-tight mt-0.5">{p.name}</h3>
                    <p className="text-xs text-muted-foreground truncate">{p.client}</p>
                  </div>
                  <Badge variant="secondary" className="shrink-0">{STATUS_LABELS[p.status]}</Badge>
                </div>
                <div className="mt-4 flex-1"><ProgressBar m={p.metrics} /></div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{p.metrics?.processed || 0}/{p.metrics?.total_target || 0} {p.metrics?.unit || "berkas"}</span>
                  <RiskBadge risk={p.metrics?.risk} />
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      
    </div>
  );
}

function Field({ label, v, on, type = "text", testid, step }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input type={type} step={step} value={v} onChange={on} data-testid={testid} />
    </div>
  );
}
